import React, { useContext, useRef, useState, useEffect } from "react";
import KRGlue from "@lyracom/embedded-form-glue";
import {
  getSubscriptionPaymentStatusCallback,
  PAYMENT_GATEWAY_PUBLIC_KEY,
  postCreateSubscriptionPaymentForm,
} from "../../core/api/Api";
import { useTranslation } from "react-i18next";
import { showToast } from "../../core/common-service/CommonService";
import useLangNavigate from "../../components/common/useLangNavigate";
import { getCurrencyForLocale, formatPrice, getLocaleFromPath, DEFAULT_LOCALE } from "../../config/locales";
import { trackCheckoutEvent } from "../../core/utils/trackCheckoutEvent";
import { useLocation } from "react-router-dom";
import { PAYMENT_GATEWAY_ENDPOINT } from "../../core/constants/constants";
import { StoreContext } from "../../core/context/StoreContext";

const Subscriptions = () => {
  const { triggerSubscriptionRefresh } = useContext(StoreContext);
  const mounted = useRef(true);
  const location = useLocation();
  const { t } = useTranslation();
  const [message, setMessage] = useState("");
  const navigate = useLangNavigate();
  const { i18n } = useTranslation();
  const activeLocale = getLocaleFromPath(window.location.pathname) ?? DEFAULT_LOCALE;
  const currency = getCurrencyForLocale(activeLocale);
  const couponCode = location.state?.couponCode || "";
  const initiationAmountRef = useRef(null);
  const onInit = useRef(true);

  useEffect(() => {
    if (!onInit.current) return;
    onInit.current = false;
    window.scroll(0, 0);
    async function setupPaymentForm() {
      const endpoint = PAYMENT_GATEWAY_ENDPOINT;
      const publicKey = PAYMENT_GATEWAY_PUBLIC_KEY;

      try {
        postCreateSubscriptionPaymentForm({ coupon_code: couponCode })
          .then(async (res) => {
            const response = res?.data?.success;
            if (response) {
              const formToken = response?.formToken;
              const receivedOrderId = response?.order_id;
              const couponApplied = response?.coupon_applied;
              const finalAmount = response?.final_amount;
              initiationAmountRef.current = finalAmount ?? null;

              // Show success message if coupon was applied
              if (couponApplied && finalAmount) {
                showToast({
                  type: "success",
                  message:
                    t("coupon_applied_successfully") + ` - ${formatPrice(finalAmount, activeLocale)}`,
                });
              }

              const { KR } = await KRGlue.loadLibrary(
                endpoint,
                publicKey,
              ); /* Load the remote library */

              await KR.setFormConfig({
                /* set the minimal configuration */
                formToken,
                "kr-language":
                  i18n.language /* to update initialization parameter */,
              });

              await KR.renderElements(
                "#myPaymentForm",
              ); /* Render the payment form into myPaymentForm div */

              await KR.onSubmit(async (res) => {
                const orderStatus = res.clientAnswer.orderStatus;
                if (orderStatus === "PAID") {
                  triggerSubscriptionRefresh();
                  showToast({
                    type: "success",
                    message: t("subscribe.subscribed_successfully"),
                  });
                  const lang = activeLocale;
                  getSubscriptionPaymentStatusCallback(receivedOrderId)
                    .then((res) => {
                      const response = res?.data?.success;
                      if (response) {
                        trackCheckoutEvent({
                          eventType: "subscription",
                          transactionId: receivedOrderId,
                          value: initiationAmountRef.current ?? response?.price ?? 0,
                          items: [
                            {
                              item_id:
                                response?.subscriptionId ||
                                `sub_${receivedOrderId}`,
                              item_name:
                                response?.subscriptionName ||
                                "Monthly Subscription",
                              item_category: "Subscription",
                              price: initiationAmountRef.current ?? response?.price ?? 0,
                              quantity: 1,
                            },
                          ],
                          output: "success",
                        });
                      }
                      window.location.href = `/${lang}/thankyou/purchase?order=${receivedOrderId}`;
                    })
                    .catch((e) => {
                      trackCheckoutEvent({
                        eventType: "subscription",
                        transactionId: receivedOrderId,
                        value: initiationAmountRef.current ?? e?.response?.data?.price ?? 0,
                        items: [
                          {
                            item_id: `sub_${receivedOrderId}`,
                            item_name: "Monthly Subscription",
                            item_category: "Subscription",
                            price: initiationAmountRef.current ?? e?.response?.data?.price ?? 0,
                            quantity: 1,
                          },
                        ],
                        output: "fail",
                      });
                      window.location.href = `/${lang}/thankyou/purchase?order=${receivedOrderId}`;
                    });
                }
                return false; // Return false to prevent the redirection
              });

              await KR.onError(async (res) => {
                getSubscriptionPaymentStatusCallback(receivedOrderId)
                  .then((resp) => {
                    trackCheckoutEvent({
                      eventType: "subscription",
                      transactionId: receivedOrderId,
                      value: initiationAmountRef.current ?? resp?.data?.price ?? 0,
                      items: [
                        {
                          item_id: `sub_${receivedOrderId}`,
                          item_name: "Monthly Subscription",
                          item_category: "Subscription",
                          price: initiationAmountRef.current ?? resp?.data?.price ?? 0,
                          quantity: 1,
                        },
                      ],
                      output: "fail",
                    });
                  })
                  .catch((error) => {
                    let price = 0;
                    if (error?.response?.data?.price !== undefined) {
                      price = error.response.data.price;
                    }
                    trackCheckoutEvent({
                      eventType: "subscription",
                      transactionId: receivedOrderId,
                      value: initiationAmountRef.current ?? price,
                      items: [
                        {
                          item_id: `sub_${receivedOrderId}`,
                          item_name: "Monthly Subscription",
                          item_category: "Subscription",
                          price: initiationAmountRef.current ?? price,
                          quantity: 1,
                        },
                      ],
                      output: "fail",
                    });
                  });
              });
            }
          })
          .catch((error) => {
            navigate(-1);
            setMessage(error);
          });
      } catch (error) {
        navigate(-1);
        setMessage(error);
      }
    }
    setupPaymentForm();
    return () => {
      mounted.current = false;
    };
  }, [i18n.language, navigate, t, couponCode]);

  return (
    <section className="aai-features py-100 payment">
      <div className="form">
        <div className="d-flex align-items-center justify-content-center">
          <div id="myPaymentForm">
            <div className="kr-smart-form" kr-card-form-expanded="true"></div>
          </div>
          <div data-test="payment-message">{message}</div>
        </div>
      </div>
    </section>
  );
};

export default Subscriptions;
